/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { KeyboardAvoidingView, type KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { ModalFormScroll } from '../../shared/modals/ModalFormScroll'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { StatusPill } from '../../shared/surface-cards/StatusPill'
import type { StatusPillTone } from '../../shared/surface-cards/StatusPill';
import { AttachmentPreviewModal } from '../../shared/attachments/components/AttachmentPreviewModal';
import type { ExpenseRow, ExpenseSettlementRow, ExpenseAttachmentRow } from '../domain/expenseTypes';
import { expenseErpCopy } from '../domain/expenseErpCopy'
import { listSettlementsForExpense } from '../infra/expensesRepo'
import { listAttachmentsForExpense, saveExpenseAttachmentLocal, deleteExpenseAttachmentLocal } from '../infra/expenseAttachmentsRepo'
import { pickAttachmentFile } from '../../shared/attachments/infra/pickAttachmentFile';
import { isUnexpectedFailureHandled } from '../../shared/sentry/presentUnexpectedFailure';
import {
  expenseAmountResidual,
  expensePaymentStatusLabel,
  expenseSettlementMethodLabel,
  formatExpenseDateShort,
  formatExpenseDateTimeShort,
  formatExpenseMoney,
} from '../domain/expenseLabels';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { todayIsoDate } from '../infra/expenseListSql';
import { alicuotaIdLabel, purchaseDocTypeLabel } from '../domain/expenseFiscalInvoice';
import { formatCuitDisplay } from '../../fiscal/domain-api';
import { showSuccess, showError } from '../../shared/ui/appToast';
import { supabase } from '../../shared/supabase/supabaseClient';

type Props = {
  visible: boolean;
  expense: ExpenseRow | null;
  canEdit: boolean;
  canPay?: boolean;
  canVoid: boolean;
  submitting: boolean;
  resolveUserLabel: (userId: string | null | undefined) => string | null;
  onClose: () => void;
  onPay: (expense: ExpenseRow) => void;
  onConfirmVoid: (expense: ExpenseRow, reason: string) => void;
  onVoidSettlement: (settlementId: string, reason: string) => void;
  organizationId?: string;
  branchId?: string;
  createdBy?: string | null;
};

function paymentStatusTone(status: ExpenseRow['paymentStatus']): StatusPillTone {
  if (status === 'paid') return 'success';
  if (status === 'partial') return 'warning';
  return 'neutral';
}

function MobileKeyboardWrap({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  if (!enabled) return <>{children}</>;
  return (
    <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap} automaticOffset>
      {children}
    </KeyboardAvoidingView>
  );
}

export function ExpenseDetailModal({
  visible,
  expense,
  canEdit,
  canPay = canEdit,
  canVoid,
  submitting,
  resolveUserLabel,
  onClose,
  onPay,
  onConfirmVoid,
  onVoidSettlement,
  organizationId,
  branchId,
  createdBy,
}: Props) {
  const backdropPadding = useModalBackdropPadding();
  const { width: winW, height: winH } = useWindowDimensions();
  const isMobile = winW < CONTENT_WIDE_BREAKPOINT;
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);
  const [settlements, setSettlements] = useState<ExpenseSettlementRow[]>([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidSettlementId, setVoidSettlementId] = useState<string | null>(null);
  const [voidSettlementReason, setVoidSettlementReason] = useState('');
  const [attachments, setAttachments] = useState<ExpenseAttachmentRow[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState('');
  const [previewMime, setPreviewMime] = useState('application/octet-stream');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const reloadSettlements = useCallback(async () => {
    if (!expense) {
      setSettlements([]);
      return;
    }
    setLoadingSettlements(true);
    try {
      const rows = await listSettlementsForExpense(expense.id, { branchId });
      setSettlements(rows);
      setVoidSettlementId(null);
      setVoidSettlementReason('');
    } finally {
      setLoadingSettlements(false);
    }
  }, [expense, branchId]);

  const reloadAttachments = useCallback(async () => {
    if (!expense) {
      setAttachments([]);
      return;
    }
    setLoadingAttachments(true);
    try {
      const rows = await listAttachmentsForExpense(expense.id);
      setAttachments(rows);
    } finally {
      setLoadingAttachments(false);
    }
  }, [expense?.id]);

  const handleAttachReceipt = useCallback(async () => {
    if (!expense || !organizationId || !branchId) return;
    if (attachmentBusy) return;
    setAttachmentError(null);
    setAttachmentBusy(true);
    try {
      const picked = await pickAttachmentFile('expenses');
      if (!picked) return;
      await saveExpenseAttachmentLocal({
        attachmentId: crypto.randomUUID(),
        expenseId: expense.id,
        branchId,
        orgId: organizationId,
        fileName: picked.fileName,
        mimeType: picked.mimeType,
        fileSize: picked.fileSize,
        fileBase64: picked.base64,
        createdBy: createdBy ?? null,
      });
      await reloadAttachments();
      showSuccess('Comprobante adjuntado');
    } catch (e) {
      // Unexpected: pickAttachmentFile already toasted + reported.
      if (isUnexpectedFailureHandled(e)) return;
      setAttachmentError(e instanceof Error ? e.message : String(e));
    } finally {
      setAttachmentBusy(false);
    }
  }, [expense, organizationId, branchId, createdBy, reloadAttachments, attachmentBusy]);

  const handleDeleteAttachment = useCallback((attachmentId: string) => {
    setPendingDeleteId(attachmentId);
  }, []);

  const confirmDeleteAttachment = useCallback(async () => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    if (!id) return;
    setAttachmentError(null);
    setAttachmentBusy(true);
    try {
      await deleteExpenseAttachmentLocal(id);
      await reloadAttachments();
      showSuccess('Comprobante eliminado');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setAttachmentError(msg);
      showError('Error', msg);
    } finally {
      setAttachmentBusy(false);
    }
  }, [pendingDeleteId, reloadAttachments]);

  const openAttachmentUrl = useCallback(async (attachment: ExpenseAttachmentRow) => {
    try {
      let url: string;
      if (attachment.fileUrl) {
        const { data, error } = await supabase.storage
          .from('expense-receipts')
          .createSignedUrl(attachment.fileUrl, 300);
        if (error || !data?.signedUrl) {
          setAttachmentError('No pude generar el enlace del comprobante.');
          return;
        }
        url = data.signedUrl;
      } else {
        setAttachmentError('El comprobante aún no está disponible.');
        return;
      }
      setPreviewFileName(attachment.fileName);
      setPreviewMime(attachment.mimeType);
      setPreviewUri(url);
    } catch (e) {
      setAttachmentError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    if (!visible || !expense) {
      setSettlements([]);
      setVoidOpen(false);
      setVoidReason('');
      setVoidSettlementId(null);
      setVoidSettlementReason('');
      setAttachments([]);
      setAttachmentError(null);
      setAttachmentBusy(false);
      setPendingDeleteId(null);
      setPreviewUri(null);
      setPreviewFileName('');
      setPreviewMime('application/octet-stream');
      return;
    }
    void reloadSettlements();
    void reloadAttachments();
  }, [visible, expense?.id, reloadSettlements, reloadAttachments]);

  if (!expense) return null;

  const linkedToPurchase = expense.purchaseOrderId != null && expense.purchaseOrderId.length > 0;

  const residual = expenseAmountResidual(expense.amount, expense.amountPaid);
  const canRegisterPayment = canPay && expense.status === 'active' && residual > 0;
  const hasActiveSettlements = settlements.some((s) => s.status === 'active');
  // Regla de anulación (alineada con el backend): factura activa, sin pagos
  // activos y con permiso. El paymentStatus no aplica — una parcial sin pagos
  // activos es un estado inconsistente que se puede corregir anulando.
  const canVoidInvoice =
    !loadingSettlements &&
    canVoid &&
    expense.status === 'active' &&
    !hasActiveSettlements &&
    !linkedToPurchase;
  const voidSettlementTarget = settlements.find((s) => s.id === voidSettlementId) ?? null;
  const closeLocked = submitting || attachmentBusy;
  const handleRequestClose = () => {
    if (closeLocked) return;
    onClose();
  };

  const canAttach = canEdit && expense.status === 'active';
  // Acciones de la factura — arriba, como el proto: lo importante primero.
  const detailActionsPanel = !isMobile && (canRegisterPayment || canAttach || canVoidInvoice) ? (
    <View style={styles.secBlock}>
      <Text style={styles.secTitle}>Acciones</Text>
      <View style={modalExperience.detailActionsGrid}>
          {canRegisterPayment ? (
            <TouchableOpacity
              style={[modalExperience.detailActionBtn, modalExperience.detailActionBtnPrimary, styles.actionBtnFull]}
              onPress={() => onPay(expense)}
              accessibilityRole="button"
              accessibilityLabel={expenseErpCopy.actionRegisterPayment}
              testID="gastos.detail.register-payment"
            >
              <Ionicons name="cash-outline" size={18} color="#FFFFFF" />
              <Text style={modalExperience.detailActionTextPrimary}>
                {expenseErpCopy.actionRegisterPayment}
              </Text>
            </TouchableOpacity>
          ) : null}
          {canAttach ? (
            <TouchableOpacity
              style={modalExperience.detailActionBtn}
              onPress={handleAttachReceipt}
              disabled={attachmentBusy || submitting}
              accessibilityRole="button"
              accessibilityLabel={expenseErpCopy.attachmentAddLabel}
              testID="gastos.detail.attach"
            >
              <Ionicons name="attach-outline" size={18} color={ticketColors.ink} />
              <Text style={modalExperience.detailActionText}>{expenseErpCopy.attachmentAddLabel}</Text>
            </TouchableOpacity>
          ) : null}
          {canVoidInvoice ? (
            <TouchableOpacity
              style={[modalExperience.detailActionBtn, modalExperience.detailActionBtnDanger]}
              onPress={() => setVoidOpen(true)}
              accessibilityRole="button"
              accessibilityLabel={expenseErpCopy.actionVoidBill}
              testID="gastos.detail.void"
            >
              <Ionicons name="trash-outline" size={18} color={ticketColors.bad} />
              <Text style={modalExperience.detailActionTextDanger}>{expenseErpCopy.actionVoidBill}</Text>
            </TouchableOpacity>
          ) : null}
      </View>
    </View>
  ) : null;

  const createdByLabel = resolveUserLabel(expense.createdBy);
  const overdueDays =
    expense.dueDate && expense.paymentStatus !== 'paid' && expense.status === 'active'
      ? Math.floor((Date.parse(todayIsoDate()) - Date.parse(expense.dueDate)) / 86400000)
      : 0;
  const heroMeta = [
    createdByLabel ? `Cargada por ${createdByLabel}` : null,
    expense.dueDate
      ? overdueDays > 0
        ? `venció hace ${overdueDays} ${overdueDays === 1 ? 'día' : 'días'}`
        : `vence ${formatExpenseDateShort(expense.dueDate)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const activePaymentsCount = settlements.filter((s) => s.status === 'active').length;

  const detailBodyElement = (
    <View style={styles.detailBody}>
      {linkedToPurchase ? (
        <View style={modalExperience.infoPanel} testID="gastos.detail.purchase-linked-banner">
          <Text style={styles.fiscalLine}>{expenseErpCopy.purchaseLinkedBillBanner}</Text>
        </View>
      ) : null}
      {!isMobile ? (
        <View style={styles.hero}>
          <View style={styles.heroMain}>
            {expense.description ? (
              <Text style={styles.heroName}>{expense.description}</Text>
            ) : null}
            {heroMeta ? <Text style={styles.heroMeta}>{heroMeta}</Text> : null}
          </View>
          <View style={styles.heroBal}>
            <Text style={styles.heroBalLabel}>{expenseErpCopy.columnBalance}</Text>
            <Text
              style={[
                styles.heroBalValue,
                residual > 0 ? styles.heroBalValueDue : styles.heroBalValueOk,
              ]}
            >
              {formatExpenseMoney(residual)}
            </Text>
            <Text style={styles.heroBalHint}>de {formatExpenseMoney(expense.amount)}</Text>
          </View>
        </View>
      ) : null}

      <View style={styles.statRow}>
        <View style={[styles.statCell, styles.statCellDivider]}>
          <Text style={styles.statValue}>{formatExpenseMoney(expense.amount)}</Text>
          <Text style={styles.statLabel}>{expenseErpCopy.columnTotal}</Text>
        </View>
        <View style={[styles.statCell, styles.statCellDivider]}>
          <Text style={styles.statValue}>{formatExpenseMoney(expense.amountPaid)}</Text>
          <Text style={styles.statLabel}>{expenseErpCopy.columnPaid}</Text>
        </View>
        <View style={[styles.statCell, !isMobile && styles.statCellDivider]}>
          <Text style={[styles.statValue, residual > 0 && styles.statValueGold]}>
            {formatExpenseMoney(residual)}
          </Text>
          <Text style={styles.statLabel}>{expenseErpCopy.columnBalance}</Text>
        </View>
        {!isMobile ? (
          <View style={styles.statCell}>
            <Text style={styles.statValue}>{activePaymentsCount}</Text>
            <Text style={styles.statLabel}>{activePaymentsCount === 1 ? 'pago' : 'pagos'}</Text>
          </View>
        ) : null}
      </View>

      {detailActionsPanel}

      {expense.invoiceDocType != null ? (
        <View style={styles.secDivided} testID="gastos.detail.fiscal-section">
          <Text style={styles.secTitle}>Comprobante fiscal</Text>
          <View style={modalExperience.infoPanel}>
            <Text style={styles.fiscalLine}>
              {purchaseDocTypeLabel(expense.invoiceDocType)}
              {expense.invoicePv != null && expense.invoiceNumber != null
                ? ` · ${String(expense.invoicePv).padStart(4, '0')}-${String(expense.invoiceNumber).padStart(8, '0')}`
                : ''}
            </Text>
            {expense.supplierTaxId ? (
              <Text style={styles.fiscalLine}>
                {expense.invoiceSupplierName ? `${expense.invoiceSupplierName} · ` : ''}
                CUIT {formatCuitDisplay(expense.supplierTaxId)}
              </Text>
            ) : null}
            {(expense.invoiceIvaBreakdown?.alicuotas ?? []).map((a, i) => (
              <Text key={i} style={styles.fiscalLine}>
                Neto {formatExpenseMoney(a.baseImp)} · IVA {alicuotaIdLabel(a.id)}{' '}
                {formatExpenseMoney(a.importe)}
              </Text>
            ))}
            {(expense.invoiceIvaBreakdown?.impOpEx ?? 0) > 0 ? (
              <Text style={styles.fiscalLine}>
                Exento {formatExpenseMoney(expense.invoiceIvaBreakdown?.impOpEx ?? 0)}
              </Text>
            ) : null}
            {(expense.invoiceIvaBreakdown?.impTotConc ?? 0) > 0 ? (
              <Text style={styles.fiscalLine}>
                No gravado {formatExpenseMoney(expense.invoiceIvaBreakdown?.impTotConc ?? 0)}
              </Text>
            ) : null}
            {expense.invoicePerceptions ? (
              <Text style={styles.fiscalLine}>
                Percepciones:{' '}
                {([
                  ['IVA', expense.invoicePerceptions.iva],
                  ['IIBB', expense.invoicePerceptions.iibb],
                  ['nacionales', expense.invoicePerceptions.nacionales],
                  ['municipales', expense.invoicePerceptions.municipales],
                ] as Array<[string, number]>)
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => `${k} ${formatExpenseMoney(v)}`)
                  .join(' · ') || '—'}
              </Text>
            ) : null}
          </View>
        </View>
      ) : null}

      <View style={styles.secDivided}>
      <Text style={styles.secTitle} testID="gastos.detail.payments-section">{expenseErpCopy.paymentsSectionTitle}</Text>
      {loadingSettlements ? (
        <View style={modalExperience.stateBox}>
          <ActivityIndicator color={ticketColors.gold} />
          <Text style={modalExperience.stateText}>Cargando…</Text>
        </View>
      ) : settlements.length === 0 ? (
        <ModalStatePanel
          empty
          emptyTitle={expenseErpCopy.paymentsEmptyTitle}
          emptyMessage={expenseErpCopy.paymentsEmptyMessage}
        />
      ) : (
        <View>
          {settlements.map((s, index) => {
            const operatorLabel = resolveUserLabel(s.createdBy);
            const isVoided = s.status === 'voided';
            // Proto mini-row: una sola línea de meta bajo el método.
            const meta = [
              formatExpenseDateTimeShort(s.paidAt),
              operatorLabel,
              s.cashOutflowId ? 'salió de la caja' : null,
              s.note,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <View
                key={s.id}
                style={[
                  styles.miniRow,
                  index === settlements.length - 1 && styles.miniRowLast,
                  isVoided && styles.listRowVoided,
                ]}
              >
                <View style={styles.settlementHeader}>
                  <View style={styles.settlementMain}>
                    <Text style={[modalExperience.listRowTitle, isVoided && styles.voidedText]}>
                      {expenseSettlementMethodLabel(s.method)}
                    </Text>
                    <Text style={modalExperience.listMeta} numberOfLines={2}>
                      {meta}
                    </Text>
                    {isVoided && s.voidReason ? (
                      <Text style={modalExperience.listMetaMuted}>Anulado: {s.voidReason}</Text>
                    ) : null}
                  </View>
                  <Text style={[styles.payValue, !isVoided && styles.payValueOk, isVoided && styles.voidedText]}>
                    {formatExpenseMoney(s.amount)}
                  </Text>
                  {isVoided ? (
                    <View style={[modalExperience.statusPill, modalExperience.statusPillCritical]}>
                      <Text style={modalExperience.statusPillTextCritical}>Anulado</Text>
                    </View>
                  ) : canVoid ? (
                    <TouchableOpacity
                      onPress={() => {
                        setVoidSettlementId(s.id);
                        setVoidSettlementReason('');
                      }}
                      disabled={submitting}
                      accessibilityRole="button"
                      accessibilityLabel="Anular pago"
                      testID="gastos.detail.void-settlement"
                    >
                      <Text style={styles.payActText}>Anular</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              </View>
            );
          })}
        </View>
      )}
      </View>

      <View style={styles.secDivided}>
      <Text style={styles.secTitle}>{expenseErpCopy.attachmentLabel}</Text>
      {attachmentError ? (
        <Text style={styles.attachError}>{attachmentError}</Text>
      ) : null}
      {loadingAttachments ? (
        <View style={modalExperience.stateBox}>
          <ActivityIndicator color={ticketColors.gold} />
          <Text style={modalExperience.stateText}>Cargando…</Text>
        </View>
      ) : attachments.length > 0 ? (
        <View style={styles.attachmentList}>
          {attachments.map((a) => (
            <View key={a.id} style={styles.attachmentRow}>
              <TouchableOpacity
                onPress={() => openAttachmentUrl(a)}
                accessibilityRole="button"
                accessibilityLabel={expenseErpCopy.attachmentViewLabel}
                style={styles.attachmentInfo}
              >
                <Ionicons
                  name={a.mimeType.startsWith('image/') ? 'image-outline' : 'document-text-outline'}
                  size={20}
                  color={ticketColors.ink2}
                />
                <View style={styles.attachmentTextCol}>
                  <Text style={styles.attachmentFileName} numberOfLines={1}>
                    {a.fileName}
                  </Text>
                  <Text style={styles.attachmentMeta}>{expenseErpCopy.attachmentViewLabel}</Text>
                </View>
              </TouchableOpacity>
              {canEdit && expense.status === 'active' && (
                <TouchableOpacity
                  onPress={() => handleDeleteAttachment(a.id)}
                  disabled={attachmentBusy}
                  accessibilityRole="button"
                  accessibilityLabel={expenseErpCopy.attachmentDeleteLabel}
                  style={styles.deleteBtn}
                >
                  <Ionicons name="trash-outline" size={16} color={ticketColors.bad} />
                </TouchableOpacity>
              )}
            </View>
          ))}
        </View>
      ) : isMobile && canEdit && expense.status === 'active' ? (
        <View style={styles.attachEmpty}>
          <TouchableOpacity
            onPress={handleAttachReceipt}
            disabled={attachmentBusy || submitting}
            accessibilityRole="button"
            accessibilityLabel={expenseErpCopy.attachmentAddLabel}
            style={styles.attachCta}
          >
            <Ionicons name="attach-outline" size={18} color={ticketColors.gold} />
            <Text style={styles.attachCtaText}>
              {attachmentBusy ? 'Subiendo…' : expenseErpCopy.attachmentAddLabel}
            </Text>
          </TouchableOpacity>
        </View>
      ) : (
        <ModalStatePanel
          empty
          emptyTitle="Sin comprobante"
          emptyMessage="Esta factura no tiene comprobante."
        />
      )}
      {isMobile && canEdit && expense.status === 'active' && attachments.length > 0 && !attachmentBusy ? (
        <TouchableOpacity
          onPress={handleAttachReceipt}
          disabled={attachmentBusy || submitting}
          accessibilityRole="button"
          accessibilityLabel={expenseErpCopy.attachmentAddLabel}
          style={styles.attachMoreBtn}
        >
          <Ionicons name="attach-outline" size={16} color={ticketColors.gold} />
          <Text style={styles.attachMoreText}>{expenseErpCopy.attachmentAddLabel}</Text>
        </TouchableOpacity>
      ) : null}
      </View>

    </View>
  );

  return (
    <>
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={handleRequestClose} accessibilityViewIsModal>
      <View
        style={isMobile ? styles.sheetBackdrop : [posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}
      >
          {isMobile ? (
            <Pressable
              style={StyleSheet.absoluteFill}
              onPress={handleRequestClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            />
          ) : null}
          <MobileKeyboardWrap enabled={isMobile}>
          <View
            style={isMobile ? [styles.sheetCard, { maxHeight: winH * 0.96 }] : [
              posUi.modalCard,
              modalShellShared.modalCardShell,
              Platform.OS === 'web'
                ? styles.modalWidthWeb
                : modalExperience.modalWidthStandard,
              styles.modalCard,
              { maxHeight: '96%' },
            ]}
          >
            {isMobile ? <View style={styles.grabber} /> : null}
            <View style={isMobile ? styles.sheetHeader : undefined}>
            <ModalScreenHeader
              onClose={handleRequestClose}
              kicker={expenseErpCopy.billDetailKicker}
              title={expense.supplierName ?? expense.categoryName ?? expense.categorySlug ?? expenseErpCopy.billDetailTitle}
              subtitle={[
                formatExpenseDateShort(expense.expenseDate),
                expense.dueDate ? `vence ${formatExpenseDateShort(expense.dueDate)}` : null,
                expense.receiptRef ? `Ref. ${expense.receiptRef}` : null,
                expense.categoryName && expense.supplierName ? expense.categoryName : null,
              ].filter(Boolean).join(' · ')}
              trailing={
                <StatusPill
                  label={expense.status === 'voided' ? expenseErpCopy.statusVoided : expensePaymentStatusLabel(expense.paymentStatus)}
                  tone={expense.status === 'voided' ? 'danger' : paymentStatusTone(expense.paymentStatus)}
                  compact
                />
              }
            />
            </View>

          {isMobile ? (
            <ModalAwareScrollView
              ref={scrollRef}
              style={styles.sheetScroll}
              contentContainerStyle={styles.sheetScrollContent}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
              bottomOffset={spacing.sm}
            >
              <View ref={scrollContentRef} collapsable={false}>
                {detailBodyElement}
              </View>
            </ModalAwareScrollView>
          ) : (
            <ModalFormScroll
              ref={scrollRef}
              contentRef={scrollContentRef}
              contentContainerStyle={styles.deskBodyPad}
              keyboardShouldPersistTaps="handled"
            >
              {detailBodyElement}
            </ModalFormScroll>
          )}

          {!isMobile ? (
            <View style={styles.deskFoot}>
              <TouchableOpacity
                style={[styles.deskFootBtn, styles.deskFootGhost]}
                onPress={handleRequestClose}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                <Text style={styles.deskFootGhostText}>Cerrar</Text>
              </TouchableOpacity>
              {canRegisterPayment ? (
                <TouchableOpacity
                  style={[styles.deskFootBtn, styles.deskFootPrimary]}
                  onPress={() => onPay(expense)}
                  accessibilityRole="button"
                  accessibilityLabel={expenseErpCopy.actionRegisterPayment}
                  testID="gastos.detail.register-payment"
                >
                  <Text style={styles.deskFootPrimaryText}>{expenseErpCopy.actionRegisterPayment}</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}

          {isMobile ? (
            <View style={[styles.mobileFooter, { paddingBottom: Math.max(insets.bottom, 20) }]}>
              {canVoidInvoice ? (
                <TouchableOpacity
                  style={[styles.mobileActionBtn, styles.mobileActionDanger]}
                  onPress={() => setVoidOpen(true)}
                  accessibilityRole="button"
                  accessibilityLabel={expenseErpCopy.actionVoidBill}
                  testID="gastos.detail.void"
                >
                  <Ionicons name="trash-outline" size={18} color={ticketColors.bad} />
                  <Text style={styles.mobileActionDangerText}>{expenseErpCopy.actionVoidBill}</Text>
                </TouchableOpacity>
              ) : null}
              {canRegisterPayment ? (
                <TouchableOpacity
                  style={[styles.mobileActionBtn, styles.mobileActionPrimary]}
                  onPress={() => onPay(expense)}
                  accessibilityRole="button"
                  accessibilityLabel={expenseErpCopy.actionRegisterPayment}
                  testID="gastos.detail.register-payment"
                >
                  <Ionicons name="cash-outline" size={18} color="#FFFFFF" />
                  <Text style={styles.mobileActionPrimaryText}>
                    {expenseErpCopy.actionRegisterPayment}
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}
          </View>
          </MobileKeyboardWrap>
        <OperationalConfirmModal
          visible={pendingDeleteId !== null}
          title="Eliminar comprobante"
          message="Se borra el comprobante. No se puede deshacer."
          confirmLabel="Eliminar"
          destructive
          iconName="trash-outline"
          presentation="overlay"
          loading={attachmentBusy}
          onClose={() => setPendingDeleteId(null)}
          onConfirm={confirmDeleteAttachment}
        />
        <OperationalConfirmModal
          visible={voidOpen}
          kicker="Gastos"
          title="Anular factura"
          subtitle={`${expense.supplierName ?? expense.categoryName ?? 'La factura'} · ${formatExpenseMoney(expense.amount)} · queda el registro anulado`}
          message={`${expense.supplierName ?? expense.categoryName ?? 'La factura'} · ${formatExpenseMoney(expense.amount)} — queda el registro anulado.`}
          hideMessage
          confirmLabel="Anular factura"
          destructive
          presentation="overlay"
          loading={submitting}
          confirmDisabled={voidReason.trim().length < 3}
          confirmTestID="gastos.detail.void-confirm"
          onClose={() => {
            setVoidOpen(false);
            setVoidReason('');
          }}
          onConfirm={() => onConfirmVoid(expense, voidReason.trim())}
        >
          <View style={modalExperience.warningPanel}>
            <Text style={styles.warnBoxText}>No se puede deshacer. Si tiene pagos activos, anulalos primero.</Text>
          </View>
          <ModalFormField label="Motivo de anulación" required>
            <TextInput
              style={[modalExperience.formInput, modalExperience.formInputMultiline]}
              value={voidReason}
              onChangeText={setVoidReason}
              placeholder="Describí por qué se anula esta factura"
              placeholderTextColor={ticketColors.ink3}
              multiline
            />
            {voidReason.trim().length < 3 ? (
              <Text style={styles.fieldHint} testID="gastos.detail.void-reason-hint">
                {expenseErpCopy.voidReasonMinHint}
              </Text>
            ) : null}
          </ModalFormField>
        </OperationalConfirmModal>
        <OperationalConfirmModal
          visible={voidSettlementId !== null}
          kicker="Gastos"
          title="Anular pago"
          subtitle={
            voidSettlementTarget
              ? `El pago queda anulado — ${formatExpenseMoney(voidSettlementTarget.amount)} vuelven al saldo de la factura`
              : 'El pago queda anulado — ese monto vuelve al saldo de la factura'
          }
          message={
            voidSettlementTarget
              ? `El pago queda anulado. ${formatExpenseMoney(voidSettlementTarget.amount)} vuelven al saldo de la factura.`
              : 'El pago queda anulado. Ese monto vuelve al saldo de la factura.'
          }
          hideMessage
          confirmLabel="Anular pago"
          destructive
          presentation="overlay"
          loading={submitting}
          confirmDisabled={voidSettlementReason.trim().length < 3}
          onClose={() => {
            setVoidSettlementId(null);
            setVoidSettlementReason('');
          }}
          onConfirm={() => {
            if (voidSettlementId) {
              onVoidSettlement(voidSettlementId, voidSettlementReason.trim());
            }
          }}
        >
          {voidSettlementTarget ? (
            <View style={styles.infoBox}>
              <Text style={styles.infoBoxText}>
                {expenseSettlementMethodLabel(voidSettlementTarget.method)} · {formatLocalDateTime(voidSettlementTarget.paidAt)}
                {resolveUserLabel(voidSettlementTarget.createdBy) ? ` · registrado por ${resolveUserLabel(voidSettlementTarget.createdBy)}` : ''}
              </Text>
            </View>
          ) : null}
          {voidSettlementTarget?.cashOutflowId ? (
            <View style={modalExperience.warningPanel}>
              <Text style={styles.warnBoxText}>Si salió de la caja y el turno ya cerró, no se puede anular — hablalo con soporte.</Text>
            </View>
          ) : null}
          <ModalFormField label="Motivo de anulación" required>
            <TextInput
              style={[modalExperience.formInput, modalExperience.formInputMultiline]}
              value={voidSettlementReason}
              onChangeText={setVoidSettlementReason}
              placeholder="Describí por qué se anula este pago"
              placeholderTextColor={ticketColors.ink3}
              multiline
            />
            {voidSettlementReason.trim().length < 3 ? (
              <Text style={styles.fieldHint} testID="gastos.detail.void-settlement-reason-hint">
                {expenseErpCopy.voidReasonMinHint}
              </Text>
            ) : null}
          </ModalFormField>
        </OperationalConfirmModal>
      </View>
    </Modal>

      <AttachmentPreviewModal
        visible={previewUri !== null}
        uri={previewUri}
        fileName={previewFileName}
        mimeType={previewMime}
        onClose={() => setPreviewUri(null)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  sheetBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetCard: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 8,
    maxHeight: '96%',
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    width: '100%',
    overflow: 'hidden',
    zIndex: 1,
  },
  grabber: {
    width: 28,
    height: 3,
    borderRadius: 2,
    backgroundColor: ticketColors.ruleSoft,
    alignSelf: 'center',
    marginTop: 2,
    marginBottom: 12,
  },
  sheetHeader: {
    flexShrink: 0,
    paddingHorizontal: 20,
    marginBottom: 16,
  },
  sheetScroll: {
    flexShrink: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    gap: spacing.md,
  },
  mobileFooter: {
    flexShrink: 0,
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: 20,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  mobileActionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: 44,
    borderRadius: 10,
  },
  mobileActionPrimary: {
    backgroundColor: ticketColors.gold,
    borderWidth: 1,
    borderColor: ticketColors.gold,
  },
  mobileActionPrimaryText: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.goldInk,
  },
  mobileActionDanger: {
    backgroundColor: ticketColors.warnBg,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
  },
  mobileActionDangerText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.bad,
  },
  modalCard: {
    width: '100%',
    alignSelf: 'center',
    gap: spacing.sm,
  },
  modalWidthWeb: {
    width: '100%',
    maxWidth: 680,
    alignSelf: 'center',
  },
  // Proto detalle: body con aire — padding 16/20 y gap 16 entre bloques.
  detailBody: {
    gap: spacing.lg,
  },
  deskBodyPad: {
    paddingHorizontal: 20,
    paddingVertical: spacing.lg,
  },
  secBlock: {
    gap: spacing.sm,
  },
  // Proto .sec-divided: hairline arriba + padding-top 16, gap 8 al contenido.
  secDivided: {
    gap: spacing.sm,
    paddingTop: spacing.lg,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  secTitle: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  fiscalLine: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 18,
  },
  actionBtnFull: {
    flexBasis: '100%',
  },
  // Proto detalle: hero (descripción + saldo) como card tile arriba del stat-row.
  hero: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 14,
    paddingHorizontal: 18,
    paddingVertical: spacing.lg,
  },
  heroMain: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  heroName: {
    fontSize: 18,
    color: ticketColors.ink,
    fontWeight: '800',
    fontFamily: ticketFonts.sans,
  },
  heroMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 12.5,
    lineHeight: 18,
  },
  heroBal: {
    alignItems: 'flex-end',
    gap: 2,
    flexShrink: 0,
  },
  heroBalLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '800',
    textTransform: 'uppercase',
    fontSize: 10.5,
    letterSpacing: 0.6,
  },
  heroBalValue: {
    fontSize: 22,
    fontWeight: '800',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
    fontFamily: ticketFonts.sans,
  },
  heroBalValueDue: {
    color: ticketColors.gold,
  },
  heroBalValueOk: {
    color: ticketColors.ok,
  },
  heroBalHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 11,
  },
  // Proto stat-row: caja con borde + divisores internos.
  statRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    overflow: 'hidden',
  },
  statCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 6,
    gap: 1,
  },
  statCellDivider: {
    borderRightWidth: StyleSheet.hairlineWidth,
    borderRightColor: ticketColors.ruleSoft,
  },
  statValue: {
    fontSize: 15,
    color: ticketColors.ink,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
    fontFamily: ticketFonts.sans,
  },
  statValueGold: {
    color: ticketColors.gold,
  },
  statLabel: {
    fontSize: 10.5,
    color: ticketColors.ink3,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
  },
  // Proto mini-row: fila flat con hairline abajo (sin caja).
  miniRow: {
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  miniRowLast: {
    borderBottomWidth: 0,
  },
  deskFoot: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
    flexShrink: 0,
  },
  deskFootBtn: {
    flex: 1,
    minHeight: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deskFootGhost: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  deskFootGhostText: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  deskFootPrimary: {
    backgroundColor: ticketColors.gold,
  },
  deskFootPrimaryText: {
    ...typography.body,
    color: ticketColors.goldInk,
    fontWeight: '800',
  },
  infoBox: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.brandSoft,
    backgroundColor: ticketColors.paper2,
    padding: spacing.md,
  },
  infoBoxText: {
    ...typography.body,
    color: ticketColors.ink2,
    fontSize: 13,
  },
  warnBoxText: {
    ...typography.body,
    color: ticketColors.ink,
    fontSize: 13,
    fontWeight: '600',
  },
  attachEmpty: {
    gap: spacing.sm,
  },
  attachCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    minHeight: 44,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderStyle: 'dashed',
    backgroundColor: ticketColors.paper,
  },
  attachCtaText: {
    ...typography.body,
    color: ticketColors.gold,
    fontWeight: '700',
  },
  attachError: {
    color: ticketColors.bad,
    fontSize: 12,
  },
  fieldHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginTop: spacing.xs,
  },
  listRowVoided: {
    opacity: 0.85,
  },
  voidedText: {
    color: ticketColors.ink3,
    textDecorationLine: 'line-through',
  },
  settlementHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  settlementMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  payValue: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
    flexShrink: 0,
  },
  payValueOk: {
    color: ticketColors.ok,
  },
  payActText: {
    ...typography.caption,
    color: ticketColors.bad,
    fontWeight: '700',
  },
  attachmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.rule,
  },
  attachmentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  attachmentTextCol: {
    flex: 1,
    gap: 2,
  },
  attachmentFileName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  attachmentMeta: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  deleteBtn: {
    padding: spacing.xs,
    borderRadius: 8,
  },
  attachMoreBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignSelf: 'flex-start',
  },
  attachMoreText: {
    ...typography.body,
    color: ticketColors.gold,
    fontWeight: '600',
  },
  attachmentList: {
    marginVertical: 0,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
});
